import { FC } from 'react';
import { Col, PageHeader, Row, Select, Space, Tag, Tooltip, Typography } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { Link, useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { FaRegCopy } from 'react-icons/fa';
import { getStatusColor } from 'utils/string.utils';
import BillableToggle from 'components/BillableToggle';
import appRoutes from 'config/appRoutes';
import { UserResponse } from 'types';
import { getRequestStatusOptions } from '../../config/getRequestStatusOptions';
import EscalationBadge from '../EscalationBadge';
import style from './index.module.less';

interface RequestFormHeaderProps {
  type: 'create' | 'view' | 'draft';
  status?: string;
  request?: Record<string, any>;
  pageUrl: string;
  goBack?: boolean;
  saving?: boolean;
  hideTitle?: boolean;
  onChangeStatus?: (newStatus: string) => void;
  onChangeBillable?: (billable: boolean) => void;
}

const RequestFormHeader: FC<RequestFormHeaderProps> = ({
  type,
  status,
  request,
  pageUrl, goBack,
  saving,
  onChangeStatus,
  onChangeBillable,
  hideTitle = false
}) => {
  const user: UserResponse = useSelector((store: any) => store.auth.user);
  const history = useHistory();

  const isFreelancer = user.contact.type === 'freelancer';

  return (
    <Row align="middle" gutter={[12, 0]} className={style.headerRow}>
      {
        !hideTitle && (
          <Col flex="auto" style={{ marginBottom: 8 }}>
            <PageHeader
              title={request?.subject ?? 'Go back'}
              className="primary no-margin-header"
              style={{ padding: 0 }}
              onBack={goBack ? () => history.push(pageUrl) : undefined}
            />
            {type === 'view' && request?.subject && (
              <Typography.Text style={{ fontSize: 14, color: 'inherit', marginLeft: 35 }}>
                {request?.title}
              </Typography.Text>
            )}
          </Col>
        )
      }

      {type !== 'create' ? (
        <Col style={{
          ...(hideTitle && { marginLeft: 'auto', marginBottom: '16px' })
        }}>
          <Space size={14}>
            {saving && <LoadingOutlined className="primary" style={{ fontSize: 22, marginTop: 4 }} spin />}
            {type === 'view' && request && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <BillableToggle
                  value={request.billable}
                  onChange={onChangeBillable}
                  size={18}
                  resource="request"
                  disabled={isFreelancer || !onChangeBillable}
                />
              </div>
            )}
            {type === 'view' && request && (
              <div>
                <EscalationBadge item={request} />
              </div>
            )}
            {type === 'view' && (
              <div>
                <Select
                  value={status}
                  className="ant-form-input-plan ant-tag-select"
                  style={{ minWidth: 140 }}
                  disabled={isFreelancer}
                  options={getRequestStatusOptions(false).map((item) => ({
                    ...item,
                    display: item.label,
                    label: (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'end', height: '100%' }}>
                        <Tag style={{ marginRight: 0 }} color={getStatusColor(item.value)}>
                          {item.label}
                        </Tag>
                      </div>
                    ),
                  }))}
                  onChange={(item) => {
                    onChangeStatus?.(item as string);
                  }}
                />
              </div>
            )}

            {!isFreelancer && (
              <Tooltip title="Duplicate">
                <Link to={`${appRoutes.REQUESTS}/${request?.uuid}/duplicate`}>
                  <FaRegCopy />
                </Link>
              </Tooltip>
            )}
          </Space>
        </Col>
      ) : undefined}
    </Row>
  );
};

export default RequestFormHeader;
